<template>
  <section id="hero" class="hero-section">
    <div class="main-content-wrapper">
      <div class="hero-content">
        <h1 class="animated-title">Transforming Higher Education with Innovative Software Solutions</h1>
        
        <!-- Text Carousel -->
        <div class="text-carousel">
          <!-- Text Block 1 (Original) -->
          <p class="carousel-text" :class="{ active: activeTextSlide === 0, inactive: activeTextSlide !== 0 }">
            Eduval specializes in academic institution software including accreditation 
            and assessment management systems, faculty information systems, and advising 
            and risk assessment management solutions. Our comprehensive suite of tools 
            empowers educational institutions to streamline operations and enhance student success.
          </p>
          
          <!-- Text Block 2 -->
          <p class="carousel-text" :class="{ active: activeTextSlide === 1, inactive: activeTextSlide !== 1 }">            
            <strong>Our Optimized Solutions are designed to:</strong><br>
            1- Reach the maximum threshold of effectiveness and productivity<br>
            2- Reach the maximum threshold of efficiency<br>
            3- Reach the minimum threshold of errors<br>
            4- Effectively deploy proper measures and controls<br>
            5- Provide adequate support and business continuity
          </p>
          
          <!-- Text Block 3 -->
          <p class="carousel-text" :class="{ active: activeTextSlide === 2, inactive: activeTextSlide !== 2 }">
            <strong>Our ecosystem, with minimal manual intervention, will provide you with data that is:</strong><br><br>
            Valid, Timely, Fit for purpose, Accessible, Cost-effective, Accurate, Relevant, 
            Has the right level of detail, Has a source of confidence & Understandable by the user.
          </p>
          
          <!-- Text Navigation Dots -->
          <div class="text-nav">
            <span 
              v-for="n in 3" 
              :key="n-1" 
              :class="{ active: activeTextSlide === n-1 }"
              @click="setActiveTextSlide(n-1)">
            </span>
          </div>
        </div>
        
        <button class="demo-btn" @click="openDemoModal">
          <span>Request a Demo</span>
          <i class="fas fa-arrow-right"></i>
        </button>
      </div>
      
      <div class="right-section">
        <!-- Simple Slider -->
        <div class="hero-slider">
          <!-- Slide 1 -->
          <div class="slide" v-show="activeSlide === 0">
            <div class="slide-point">
              <p><i class="fas fa-shield-alt"></i> Attain quality assurance & compliance in a seamless way.</p>
            </div>
          </div>
          
          <!-- Slide 2 -->
          <div class="slide" v-show="activeSlide === 1">
            <div class="slide-point">
              <p><i class="fas fa-hand-pointer"></i> Minimize user intervention and data entry needs.</p>
            </div>
          </div>
          
          <!-- Slide 3 -->
          <div class="slide" v-show="activeSlide === 2">
            <div class="slide-point">
              <p><i class="fas fa-exclamation-triangle"></i> Identify potential problems before they occur.</p>
            </div>
          </div>
          
          <!-- Slider Navigation -->
          <div class="slide-nav">
            <span 
              v-for="n in 3" 
              :key="n-1" 
              :class="{ active: activeSlide === n-1 }"
              @click="setActiveSlide(n-1)">
            </span>
          </div>
        </div>
        
        <div class="logo-container">
          <img src="@/assets/logo.png" alt="Eduval Logo" class="bouncing-logo" />
        </div>
      </div>
    </div>

    <!-- Hero Marquee - positioned above stats -->
    <div class="hero-marquee">
      <div class="marquee-content">
        <span><i class="fas fa-chart-line"></i> Improve institutional performance and effectiveness</span>
        <span><i class="fas fa-calendar"></i> Support ongoing planning, assessment and evaluation</span>
        <span><i class="fas fa-award"></i> Assure educational quality and continuous improvement</span>
        <span><i class="fas fa-briefcase"></i> Enhance student success and prepare future graduates</span>
        <span><i class="fas fa-medal"></i> Demonstrate compliance and achieve accreditations</span>
        <span><i class="fas fa-chart-bar"></i> Ensure data-driven decision-making and accountability</span>
        <span><i class="fas fa-clock"></i> Gain real-time insight into your data & make proactive informed decisions</span>
        <span><i class="fas fa-cog"></i> Optimize your teaching and learning & academic processes</span>
      </div>
    </div>

    <!-- Stats Bar -->
    <div class="stats-bar">
      <div class="stats-container">
        <div class="stat-item" 
          v-for="(stat, index) in stats" 
          :key="index"
          @mousemove="handleMouseMove"
          @mouseleave="handleMouseLeave"
          ref="statItems">
          <div class="stat-content">
            <i :class="stat.icon" class="stat-icon"></i>
            <div class="stat-number">{{ stat.value }}</div>
            <div class="stat-label">{{ stat.label }}</div>
            <div class="stat-shine"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Demo Request Modal -->
    <Modal 
      :show="showDemoModal" 
      @close="closeDemoModal" 
      title="Request a Demo" 
      size="large">
      <DemoRequestForm />
    </Modal>
  </section>
</template>

<script>
import Modal from './Modal.vue';
import DemoRequestForm from './DemoRequestForm.vue';

export default {
  name: 'HeroSection',
  components: {
    Modal,
    DemoRequestForm
  },
  data() {
    return {
      showDemoModal: false,
      activeSlide: 0,
      activeTextSlide: 0,
      slideshowInterval: null,
      textSlideshowInterval: null,
      stats: [
        { icon: 'fas fa-calendar-alt', value: '25+', label: 'Years of Experience' },
        { icon: 'fas fa-globe-americas', value: '17+', label: 'Countries' },
        { icon: 'fas fa-project-diagram', value: '300+', label: 'Completed Projects' },
        { icon: 'fas fa-smile', value: '100+', label: 'Happy Clients' }
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.startSlideshow();
    this.startTextSlideshow();
    this.animateOnScroll();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    this.stopSlideshow();
    this.stopTextSlideshow();
  },
  methods: {
    handleScroll() {
      const heroSection = document.querySelector('.hero-section');
      const scrollPosition = window.scrollY;
      
      if (heroSection) {
        heroSection.style.backgroundPositionY = scrollPosition * 0.5 + 'px';
      }
    },
    animateOnScroll() {
      const section = document.querySelector('.hero-section');
      if (!section) return;
      
      section.classList.add('animate');
    },
    openDemoModal() {
      this.showDemoModal = true;
    },
    closeDemoModal() {
      this.showDemoModal = false;
    },
    setActiveSlide(index) {
      this.activeSlide = index;
      this.restartSlideshow();
    },
    startSlideshow() {
      this.slideshowInterval = setInterval(() => {
        this.activeSlide = (this.activeSlide + 1) % 3;
      }, 5000);
    },
    stopSlideshow() {
      if (this.slideshowInterval) {
        clearInterval(this.slideshowInterval);
        this.slideshowInterval = null;
      }
    },
    restartSlideshow() {
      this.stopSlideshow();
      this.startSlideshow();
    },
    setActiveTextSlide(index) {
      this.activeTextSlide = index;
      this.restartTextSlideshow();
    },
    startTextSlideshow() {
      this.textSlideshowInterval = setInterval(() => {
        this.activeTextSlide = (this.activeTextSlide + 1) % 3;
      }, 8000); // Slightly longer interval for text
    },
    stopTextSlideshow() {
      if (this.textSlideshowInterval) {
        clearInterval(this.textSlideshowInterval);
        this.textSlideshowInterval = null;
      }
    },
    restartTextSlideshow() {
      this.stopTextSlideshow();
      this.startTextSlideshow();
    },
    handleMouseMove(e) {
      const statItem = e.currentTarget;
      const statRect = statItem.getBoundingClientRect();
      const statContent = statItem.querySelector('.stat-content');
      
      // Get cursor position relative to the stat item
      const x = e.clientX - statRect.left;
      const y = e.clientY - statRect.top;
      
      // Calculate the center point of the stat item
      const centerX = statRect.width / 2;
      const centerY = statRect.height / 2;
      
      // Calculate rotation values based on cursor position
      const rotateY = ((x - centerX) / 10) * -1; // Reverse for natural feel
      const rotateX = (y - centerY) / 10;
      
      // Apply the 3D transformation
      statContent.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.05, 1.05, 1.05)`;
      
      // Position the shine effect to follow the cursor
      const shine = statItem.querySelector('.stat-shine');
      shine.style.opacity = '0.25';
      shine.style.transform = `translate(${x}px, ${y}px)`;
    },
    handleMouseLeave(e) {
      const statItem = e.currentTarget;
      const statContent = statItem.querySelector('.stat-content');
      
      // Reset the 3D transformation
      statContent.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      
      // Hide the shine effect
      const shine = statItem.querySelector('.stat-shine');
      shine.style.opacity = '0';
    }
  }
}
</script>

<style scoped>
.hero-section {
  display: flex;
  flex-direction: column;
  padding: 7rem 0 0 0;
  background-color: #fcfaf2;
  background-image: radial-gradient(circle at 10% 20%, rgba(209, 192, 110, 0.05) 0%, rgba(209, 192, 110, 0.1) 90%),
                    linear-gradient(to right, rgba(252, 250, 242, 1) 0%, rgba(252, 250, 242, 0.8) 40%, rgba(252, 250, 242, 0.1) 100%),
                    url('@/assets/hero-image.jpg');
  background-size: auto, auto, cover;
  background-position: center, center, right center;
  background-repeat: no-repeat;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: -10%;
  right: -5%;
  width: 300px;
  height: 300px;
  border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%;
  background: linear-gradient(45deg, #d1c06e, #e0d394);
  opacity: 0.07;
  animation: blob 15s infinite linear alternate;
  z-index: 0;
}

.hero-section::after {
  content: '';
  position: absolute;
  bottom: -15%;
  left: -10%;
  width: 400px;
  height: 400px;
  border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
  background: linear-gradient(45deg, #e0d394, #beab4f);
  opacity: 0.05;
  animation: blob 20s infinite linear alternate-reverse;
  z-index: 0;
}

.main-content-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3rem 3rem 5rem;
  width: 100%;
  position: relative;
  z-index: 2;
}

.hero-content {
  flex: 1.2;
  max-width: 600px;
  padding-right: 2rem;
  z-index: 2;
}

.right-section {
  flex: 0.8;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding-left: 1rem;
  height: 500px;
  z-index: 2;
}

/* Animated Title Styles */
.animated-title {
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  animation: fadeInUp 1s ease-out;
}

.animated-title::after {
  content: '';
  display: block;
  width: 50px;
  height: 4px;
  background: linear-gradient(to right, #d1c06e, #e0d394);
  margin-top: 15px;
  border-radius: 2px;
  animation: growWidth 1.5s ease-out forwards;
}

.animated-text {
  font-size: 1.1rem;
  color: #555;
  line-height: 1.6;
  margin-bottom: 2rem;
  animation: fadeInUp 1.5s ease-out;
}

.demo-btn {
  padding: 1rem 2rem;
  background: linear-gradient(45deg, #d1c06e, #e0d394);
  color: white;
  border: none;
  border-radius: 30px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 15px rgba(209, 192, 110, 0.3);
  animation: fadeInUp 2s ease-out;
  overflow: hidden;
  position: relative;
  z-index: 5;
}

.demo-btn:hover {
  transform: translateY(-5px);
  box-shadow: 0 7px 20px rgba(209, 192, 110, 0.5);
  background: linear-gradient(45deg, #beab4f, #d1c06e);
}

.demo-btn:active {
  transform: translateY(-2px);
}

.demo-btn i {
  transition: all 0.3s ease;
}

.demo-btn:hover i {
  transform: translateX(5px);
}

.demo-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: 0.5s;
}

.demo-btn:hover::before {
  left: 100%;
}

/* Hero Slider Styles - Completely Revised */
.hero-slider {
  position: relative;
  width: 100%;
  height: 120px;
  border-radius: 8px;
  background-color: transparent;
  box-shadow: none;
  overflow: hidden;
  z-index: 2;
}

.slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s ease;
}

.slide-point {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.slide-point p {
  font-size: 1.1rem;
  color: #fff;
  font-weight: 700;
  margin: 0;
  line-height: 1.4;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  background: rgba(0, 0, 0, 0.5);
  padding: 12px 18px;
  border-radius: 25px;
  display: inline-block;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(5px);
}

.slide-point p i {
  font-size: 1.4rem;
  color: #d1c06e;
  text-shadow: 0 0 8px rgba(255, 255, 255, 0.7);
  margin-right: 10px;
  vertical-align: middle;
  display: inline-block;
}

.slide-nav {
  position: absolute;
  bottom: 1rem;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 0.8rem;
  z-index: 5;
}

.slide-nav span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(209, 192, 110, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.slide-nav span.active {
  background-color: #d1c06e;
  transform: scale(1.3);
}

/* Logo Animation */
.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex: 1;
}

.bouncing-logo {
  width: 200px;
  height: auto;
  animation: bounce 3s ease-in-out infinite;
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 30%;
  padding: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

/* Hero Image Styles */
.hero-image-container {
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 1rem 0;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  z-index: 2;
}

.hero-image {
  width: 100%;
  max-width: 400px;
  height: auto;
  transition: transform 0.5s ease;
  object-fit: cover;
}

.hero-image-container:hover .hero-image {
  transform: scale(1.05);
}

/* Hero Marquee Styles */
.hero-marquee {
  width: 100%;
  overflow: hidden;
  background-color: rgba(33, 33, 33, 0.8);
  padding: 0.8rem 0;
  margin-bottom: 0;
  position: relative;
  z-index: 3;
}

.marquee-content {
  display: flex;
  animation: marquee 30s linear infinite;
  white-space: nowrap;
}

.marquee-content span {
  display: inline-flex;
  align-items: center;
  color: white;
  padding: 0 1.5rem;
  font-size: 0.9rem;
}

.marquee-content i {
  color: #d1c06e;
  margin-right: 0.5rem;
  font-size: 1rem;
}

/* Stats Bar Styles */
.stats-bar {
  width: 100%;
  background: linear-gradient(135deg, #d1c06e 0%, #d9cb85 100%);
  padding: 2rem 0;
  box-shadow: 0 10px 30px rgba(209, 192, 110, 0.2);
  overflow: hidden;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease-out;
  perspective: 1500px;
  position: relative;
  z-index: 2;
}

.hero-section.animate .stats-bar {
  opacity: 1;
  transform: translateY(0);
  transition-delay: 1s;
}

.stats-container {
  display: flex;
  justify-content: space-around;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  padding: 0 1rem;
}

.stat-item {
  text-align: center;
  padding: 0 1rem;
  opacity: 0;
  transform: scale(0.9);
  transition: all 0.5s ease-out;
  perspective: 1000px;
  position: relative;
  cursor: pointer;
}

.stat-content {
  padding: 1.5rem;
  transform-style: preserve-3d;
  will-change: transform;
  transition: transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
  position: relative;
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  min-width: 180px;
}

.hero-section.animate .stat-item {
  opacity: 1;
  transform: scale(1);
}

.hero-section.animate .stat-item:nth-child(1) {
  transition-delay: 1.2s;
}

.hero-section.animate .stat-item:nth-child(2) {
  transition-delay: 1.4s;
}

.hero-section.animate .stat-item:nth-child(3) {
  transition-delay: 1.6s;
}

.hero-section.animate .stat-item:nth-child(4) {
  transition-delay: 1.8s;
}

.stat-icon {
  font-size: 2rem;
  color: #fff;
  margin-bottom: 0.8rem;
  margin-right: 5px;
  transform: translateZ(40px);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  display: inline-block;
  transform: translateZ(30px);
}

.stat-number::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 3px;
  background: #fff;
  transition: width 0.8s ease-out;
}

.hero-section.animate .stat-number::after {
  width: 40px;
  transition-delay: 2s;
}

.stat-label {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  transform: translateZ(20px);
}

.stat-shine {
  position: absolute;
  top: 0;
  left: 0;
  width: 80px;
  height: 80px;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(255, 255, 255, 0) 80%
  );
  border-radius: 50%;
  opacity: 0;
  pointer-events: none;
  z-index: 3;
  transform: translate(-50%, -50%);
  transition: opacity 0.2s ease;
}

/* Add sparkle animation to stats */
.hero-section.animate .stat-content {
  overflow: hidden;
  position: relative;
}

.hero-section.animate .stat-content::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.3) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: rotate(30deg);
  animation: shinyEffect 3s infinite;
  z-index: 1;
  pointer-events: none;
}

.hero-section.animate .stat-item:nth-child(1) .stat-content::before {
  animation-delay: 0s;
}

.hero-section.animate .stat-item:nth-child(2) .stat-content::before {
  animation-delay: 0.5s;
}

.hero-section.animate .stat-item:nth-child(3) .stat-content::before {
  animation-delay: 1s;
}

.hero-section.animate .stat-item:nth-child(4) .stat-content::before {
  animation-delay: 1.5s;
}

@keyframes shinyEffect {
  0% {
    transform: scale(0, 0) rotate(30deg);
    opacity: 0;
  }
  25% {
    transform: scale(0, 0) rotate(30deg);
    opacity: 0.3;
  }
  50% {
    transform: scale(1, 1) rotate(30deg);
    opacity: 0.3;
  }
  100% {
    transform: scale(5, 5) rotate(30deg);
    opacity: 0;
  }
}

@keyframes marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
}

@keyframes growWidth {
  0% {
    width: 0;
  }
  100% {
    width: 100px;
  }
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes blob {
  0% {
    border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%;
  }
  25% {
    border-radius: 70% 30% 50% 50% / 30% 60% 40% 70%;
  }
  50% {
    border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%;
  }
  75% {
    border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
  }
  100% {
    border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%;
  }
}

/* Responsive Adjustments */
@media (max-width: 992px) {
  .hero-section {
    min-height: auto;
  }
  
  .main-content-wrapper {
    flex-direction: column;
    padding: 2rem 1.5rem 3rem;
    text-align: center;
  }
  
  .hero-content {
    max-width: 100%;
    margin-bottom: 2rem;
    padding-right: 0;
  }
  
  .right-section {
    width: 100%;
    padding-left: 0;
    gap: 2rem;
    height: auto;
  }
  
  .hero-slider {
    width: 90%;
    max-width: 500px;
  }
  
  .logo-container {
    margin-bottom: 2rem;
  }
  
  .animated-title::after {
    margin: 15px auto 0;
  }
  
  .demo-btn {
    margin: 0 auto;
  }
  
  .stats-container {
    flex-wrap: wrap;
    gap: 1.5rem;
  }
}

@media (max-width: 768px) {
  .stats-container {
    flex-direction: column;
    gap: 1.5rem;
  }
  
  .stat-item {
    padding: 0.5rem 0;
  }
  
  .stat-number {
    font-size: 2.2rem;
  }
  
  .bouncing-logo {
    width: 150px;
  }
}

/* Add text carousel styles */
.text-carousel {
  position: relative;
  width: 100%;
  min-height: 180px;
  margin-bottom: 2rem;
}

.carousel-text {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  font-size: 1.1rem;
  color: #555;
  line-height: 1.6;
  transition: opacity 0.8s ease, transform 0.8s ease;
  opacity: 0;
  transform: translateY(20px);
  pointer-events: none;
}

.carousel-text.active {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.carousel-text.inactive {
  opacity: 0;
  transform: translateY(20px);
  pointer-events: none;
}

.text-nav {
  display: flex;
  justify-content: center;
  gap: 0.8rem;
  margin-top: 10px;
  position: absolute;
  bottom: -30px;
  width: 100%;
}

.text-nav span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(209, 192, 110, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
}

.text-nav span.active {
  background-color: #d1c06e;
  transform: scale(1.3);
}

/* Ensure responsive behavior for the carousel */
@media (max-width: 992px) {
  .text-carousel {
    min-height: 280px;
  }
}

@media (max-width: 768px) {
  .text-carousel {
    min-height: 380px;
  }
}

@media (max-width: 576px) {
  .text-carousel {
    min-height: 450px;
  }
}
</style> 